import React from 'react';
import {
  Box, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import { humanMs } from '../operations/areas';

// What a fleet running between its own sites is actually judged on.
//
// Not what a plant's gate traffic is judged on, and not what a telematics page measures. This fleet
// has no turnaround, no challan and no exceptions to clear; it has a cycle it repeats. So the
// questions are whether it is turning as many runs as it should by this time of day, how long the
// cycle is taking against what it usually takes, and where inside that cycle the time is going.
//
// That last one carries the most weight and gets a panel of its own, because on this fleet more than
// half of a round trip is spent standing at a dock rather than driving. A fleet like that cannot be
// made faster by adding a lorry - and no figure built on distance would ever say so.

/** A share as a percentage, where there is something to take a share of. */
const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : null);

/**
 * How today compares with what this fleet normally has done by now.
 *
 * A count on its own cannot be read: twelve runs at four in the afternoon is a good day, and twelve
 * by ten in the morning is an exceptional one. So the tile carries the comparison, and says which
 * side of it today is on rather than leaving the reader to work it out.
 */
export const pace = (runs, expected) => {
  if (!expected) return null;
  const share = runs / expected;
  if (share >= 1) return { text: `${Math.round(share * 100)}% of the usual pace`, good: true };
  return { text: `${Math.round(share * 100)}% of the usual ${Math.round(expected)} by now`, good: false };
};

/** Where the cycle goes: standing at docks against moving between them. */
const Composition = ({ composition }) => {
  const standing = composition?.standingShare ?? 0;
  const legs = composition?.legs || [];
  if (!legs.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700}>Where the trip time goes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every leg of every trip in the last fortnight. Detention is capacity somebody can go and
        change; running time mostly is not.
      </Typography>
      <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Detention at bays</Typography>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{`${Math.round(standing * 100)}%`}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">In transit</Typography>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{`${100 - Math.round(standing * 100)}%`}</Typography>
        </Box>
      </Stack>
      <Stack gap={1}>
        {legs.map((leg) => (
          <Box key={leg.name}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2" sx={{ fontWeight: leg.kind === 'STOP' ? 600 : 400 }}>
                {leg.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {`${humanMs(leg.averageMs)} · ${Math.round(leg.share * 100)}%`}
              </Typography>
            </Stack>
            {/* Standing and moving are coloured apart, because the difference between them is the
                whole point of the panel rather than a detail of it. */}
            <Tooltip title={leg.kind === 'STOP' ? 'Detention at this point' : 'Running this stretch'}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, leg.share * 100)}
                color={leg.kind === 'STOP' ? 'warning' : 'info'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Tooltip>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

/** Which lorry turned how many, and which is standing. */
const PerVehicle = ({ rows }) => {
  if (!rows?.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700}>By truck, today</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        An unbalanced fleet shows here before it shows anywhere else.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Truck', 'Trips today', 'Status'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.vehicleId} hover>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.name}</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.runsToday}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {/* How long it has been standing, not merely that it is. A lorry idle twenty
                      minutes between runs is working; one idle since yesterday is not. */}
                  {row.running ? 'On a trip'
                    : row.idleMs == null ? 'No trips recorded'
                      : `Idle ${humanMs(row.idleMs)}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

/** Everything below the tiles on the Area movement page. */
const AreaMovementPanels = ({ day }) => {
  if (!day || !day.fleet) return null;
  return (
    <>
      <Composition composition={day.composition} />
      <PerVehicle rows={day.perVehicle} />
    </>
  );
};

export { percent };
export default AreaMovementPanels;
